import { useAppSelector } from '@/app/app.store';
import {
  useGetAllPlansQuery,
  useProceedToPaymentMutation,
} from '@/app/features/onboarding/onboarding.api';
import type {
  Plan,
  PlanModule,
} from '@/app/features/onboarding/onboarding.type';
import { CheckIcon, ChevronDownIcon } from '@/icons';
import ConfirmationModal from '@/components/ConfirmationModal';
import WithLoader from '@/components/WithLoader';
import useAuth from '@/hooks/useAuth';
import useDiscloser from '@/hooks/useDiscloser';
import { cn } from '@/lib/cn';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { capitalize, formatNumber } from '@/utils';
import { Button, Collapse, Divider, Flex } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import RightArrowIcon from '../_icons/RightArrowIcon';
import SeatIcon from '../_icons/SeatIcon';
const { Panel } = Collapse;

type Props = {
  onBack: () => void;
};

export default function PlanInfo({ onBack }: Props) {
  const [config, handleOpen, handleClose] = useDiscloser();
  const onboardInfo = useAppSelector((state) => state.onboarding);

  const { verifiedInfo } = useAuth();
  const userId = verifiedInfo?.id;

  const preSelectedOrgId = onboardInfo?.organizationInfo?.id;
  const preSelectedPlanId = onboardInfo?.planInfo?.id || '';
  const preSelectedPlanType = onboardInfo?.planInfo?.billingType || 'monthly';

  const [billingType, setBillingType] = useState(preSelectedPlanType);
  const [selectedPlan, setSelectedPlan] = useState<string>(preSelectedPlanId);

  // Fetch plans from API with yearly-only filter
  const { data, isLoading, isFetching } = useGetAllPlansQuery({
    params: {
      is_monthly: billingType === 'monthly',
      is_yearly: billingType === 'yearly',
    },
  });
  const plansData = data?.data;

  // Proceed to payment mutation
  const [proceedToPayment, proceedState] = useProceedToPaymentMutation();
  const fallbackError = 'Failed to initiate payment.';

  // Handle next button click (Proceed to Payment)
  const handleProceedToPayment = async () => {
    if (!userId) {
      toast('error', 'Please login first.');
      return;
    } else if (!preSelectedOrgId) {
      toast('error', 'Please create organization first.');
      return;
    } else if (!selectedPlan) {
      toast('error', 'Please select a plan to continue.');
      return;
    }
    const selectedPlanDetails = plansData?.find(
      (plan: Plan) => plan.id === selectedPlan,
    );
    if (!selectedPlanDetails) {
      toast('error', 'Please select a valid plan to continue');
      return;
    }

    try {
      const startDate = dayjs();
      const endDate =
        billingType === 'monthly'
          ? startDate.add(1, 'month')
          : startDate.add(1, 'year');
      const renewalDate = endDate.subtract(14, 'day');

      const res = await proceedToPayment({
        currency: 'BDT',
        is_trial: false,
        is_active: true,
        userUuid: userId,
        productCode: 'HRX',
        is_auto_renewable: true,
        is_self_onboarded: true,
        planId: selectedPlanDetails.id,
        organizationId: preSelectedOrgId,
        end_date: endDate.toISOString(),
        start_date: startDate.toISOString(),
        renewal_date: renewalDate.toISOString(),
        productId: selectedPlanDetails.product_id,
        price: parseFloat(selectedPlanDetails.price),
        redirectUrls: {
          success: 'https://staging-b2b.hrxbd.org/payment-success',
          fail: 'https://staging-b2b.hrxbd.org/payment-failed',
        },
      }).unwrap();
      //  redirectUrls: {
      //           success: "https://staging-b2b.hrxbd.org/admin/employee/dashboard?segment=subscriptions",
      //           fail: "https://staging-b2b.hrxbd.org/onboard",
      //         },
      if (res.success && res?.data?.errorMessage && res.data.redirectUrl) {
        handleOpen({
          type: 'ACTION',
          data: { message: res?.data?.errorMessage, url: res.data.redirectUrl },
        });
      } else if (res.success && res.data.redirectUrl) {
        window.location.href = res.data.redirectUrl;
      } else {
        toast('error', res?.data?.errorMessage || fallbackError);
      }
    } catch (error: any) {
      const message = getErrorMessage(error, fallbackError);
      toast('error', message);
    }
  };

  // Set default selected plan when data loads
  // useEffect(() => {
  //   if (selectedPlanId) {
  //     setSelectedPlan(selectedPlanId);
  //     return;
  //   } else if (plansData && plansData?.length > 0) {
  //     setSelectedPlan(plansData[0].id);
  //   }
  // }, [plansData]);
  return (
    <>
      {config?.type === 'ACTION' && (
        <ConfirmationModal
          title="Payment Error"
          subTitle={config?.data?.message}
          handleClose={handleClose}
          open={config?.open}
          buttonName="Pay Now"
          cancelButton="Close"
          handleSubmit={() => {
            window.location.href = config?.data?.url as string;
          }}
        />
      )}

      <div className="min-h-screen bg-transparent text-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex justify-start items-center gap-3">
            <button className="cursor-pointer" onClick={onBack}>
              <RightArrowIcon className="text-teal-500 text-2xl" />
            </button>
            <h2 className="text-[32px] font-semibold">Select Plan</h2>
          </div>
          <div className="flex gap-3 p-1 rounded-md items-center bg-theme-slate-800">
            {['monthly', 'yearly'].map((period) => (
              <Button
                key={period}
                type="text"
                onClick={() => setBillingType(period as 'monthly')}
                className={cn(
                  '!px-4 !py-1 !rounded-md !text-sm !font-medium h-8',
                  billingType === period
                    ? '!bg-white !text-neutral-700'
                    : '!bg-theme-slate-800 !text-white',
                )}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </Button>
            ))}
          </div>
        </div>
        {/* Divider */}
        <Divider className="bg-theme-slate-800" />

        <WithLoader isLoading={isFetching || isLoading}>
          <div className="gap-3 mt-5">
            {plansData?.map((plan: Plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={cn(
                  `rounded-xl p-5 bg-transparent mb-5 cursor-pointer border border-theme-slate-800 hover:border-theme-primary `,
                  selectedPlan === plan.id && 'border-theme-primary',
                )}
              >
                <Flex justify="space-between" className="mb-5">
                  <Flex align="center" gap={12}>
                    <h3 className="text-2xl font-medium">{plan?.name}</h3>
                    {plan.trial_period > 0 && (
                      <span className="px-2 py-0.5 capitalize rounded-full bg-[linear-gradient(108deg,_#694BF7_0%,_#CD41F3_97.57%)]">
                        {plan.trial_period} days Trial
                      </span>
                    )}
                  </Flex>
                  <h3 className="text-2xl font-semibold">
                    ৳{formatNumber(parseFloat(plan.price) || 0)}{' '}
                    <span className="text-xl font-medium">/{billingType}</span>
                  </h3>
                </Flex>
                <PlanPanel plan={plan} isSelected={selectedPlan === plan.id} />
              </div>
            ))}
          </div>
        </WithLoader>
        {/* Submit Button */}
        <Flex justify="space-between">
          <Button
            size="large"
            htmlType="button"
            className="min-w-[170px] btn-primary-bordered"
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            size="large"
            type="primary"
            htmlType="button"
            onClick={handleProceedToPayment}
            loading={proceedState.isLoading}
            className="btn-primary min-w-[200px]"
          >
            Proceed to Payment
          </Button>
        </Flex>
      </div>
    </>
  );
}

const PlanPanel = ({
  plan,
  isSelected,
}: {
  plan: Plan;
  isSelected: boolean;
}) => {
  const [activeKey, setActiveKey] = useState<string[]>([]);

  useEffect(() => {
    if (isSelected) {
      setActiveKey(['plan-details']);
    } else {
      setActiveKey([]);
    }
  }, [isSelected]);
  return (
    <Collapse
      ghost
      expandIconPosition="end"
      className="bg-transparent subscription-collapse"
      expandIcon={() => null}
      onChange={(key) => setActiveKey(key as string[])}
      activeKey={activeKey}
    >
      <Panel
        key="plan-details"
        header={
          <Flex align="center" justify="space-between" className="w-full">
            <Flex align="center" gap={12}>
              <h4 className="text-xl text-theme-primary">View Plan Details</h4>
              <ChevronDownIcon
                className={`text-xl text-theme-primary transition-transform mt-1 ${
                  activeKey.includes('plan-details') ? 'rotate-180' : ''
                }`}
              />
            </Flex>

            <Flex className="text-xl text-theme-primary" align="center" gap={5}>
              <SeatIcon />
              <span className="text-white text-lg">{plan.max_user} Seats</span>
            </Flex>
          </Flex>
        }
      >
        {/* Plan Description */}
        {plan.description && (
          <div className="mb-4">
            <p className="text-sm text-gray-300">{plan.description}</p>
          </div>
        )}

        {/* Plan Modules */}
        {plan.planModules && plan.planModules.length > 0 && (
          <ul className="grid grid-cols-1 gap-y-3 max-h-[450px] overflow-y-auto custom-scroll pr-2">
            {plan.planModules.map((module: PlanModule, i: number) => (
              <li key={i} className="flex items-start gap-2">
                <CheckIcon size={18} className="text-theme-primary mt-[2px]" />
                <div className="text-sm">
                  <p className="font-medium">
                    {capitalize(module.module.name)}
                  </p>
                  {module.module.description && (
                    <p className="text-gray-400 text-xs">
                      {module.module.description}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </Collapse>
  );
};
